import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Loader2, Users, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const inputCls =
  "w-full rounded-xl border border-[color:var(--hairline)] bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ink)]";

const STATI = [
  { value: "attivo", label: "Attivo (visibile)" },
  { value: "in_trattativa", label: "In trattativa (nascosto)" },
  { value: "bozza", label: "Bozza (nascosto)" },
];

const STATO_STYLE = {
  attivo: { background: "var(--volt)", color: "var(--ink)" },
  in_trattativa: { background: "#B45309", color: "#fff" },
  bozza: { background: "var(--bg-subtle)", color: "var(--text-2)" },
};

const emptyExpert = {
  id: "",
  nome: "",
  cognome: "",
  ruolo: "",
  brand: "",
  citta: "",
  logo_url: "",
  foto_url: "",
  bio: "",
  bio_compact: "",
  aree: "",
  citazione: "",
  stato: "bozza",
  ordine: 0,
};

/** Gestione garanti/esperti: tabella + crea/modifica + cambio stato (senza codice). */
export const ExpertsManager = () => {
  const [experts, setExperts] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [dialog, setDialog] = useState(null); // {mode:'create'|'edit', data}
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(() => {
    api
      .get("/admin/experts")
      .then((r) => setExperts(r.data || []))
      .catch((e) => {
        toast.error(errMsg(e));
        setExperts([]);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changeStato = async (expert, stato) => {
    setBusyId(expert.id);
    try {
      await api.patch(`/admin/experts/${expert.id}/stato`, { stato });
      toast.success(`"${expert.nome} ${expert.cognome}" → ${stato === "attivo" ? "Attivo (visibile)" : stato === "in_trattativa" ? "In trattativa (nascosto)" : "Bozza (nascosto)"}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  const openCreate = () => setDialog({ mode: "create", data: { ...emptyExpert } });
  const openEdit = (e) =>
    setDialog({
      mode: "edit",
      data: {
        ...emptyExpert,
        ...e,
        aree: Array.isArray(e.aree) ? e.aree.join(", ") : "",
        citazione: (e.citazioni || [])[0]?.testo || "",
        logo_url: e.logo_url || "",
        foto_url: e.foto_url || "",
      },
    });

  const save = async () => {
    const d = dialog.data;
    if (!d.nome.trim() || !d.cognome.trim()) {
      toast.error("Nome e cognome sono obbligatori.");
      return;
    }
    const payload = {
      ...d,
      aree: d.aree ? d.aree.split(",").map((s) => s.trim()).filter(Boolean) : [],
      ordine: Number(d.ordine) || 0,
      logo_url: d.logo_url || null,
      foto_url: d.foto_url || null,
    };
    setSaving(true);
    try {
      if (dialog.mode === "edit") {
        await api.put(`/admin/experts/${d.id}`, payload);
        toast.success("Esperto aggiornato.");
      } else {
        await api.post("/admin/experts", payload);
        toast.success("Esperto creato.");
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const e = confirmDel;
    setConfirmDel(null);
    setBusyId(e.id);
    try {
      await api.delete(`/admin/experts/${e.id}`);
      toast.success("Esperto eliminato.");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusyId(null);
    }
  };

  const setField = (k, v) => setDialog((prev) => ({ ...prev, data: { ...prev.data, [k]: v } }));

  return (
    <div className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] p-5" data-testid="admin-experts-manager">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4" aria-hidden="true" />
          <h3 className="text-xs font-extrabold tracking-[0.14em] uppercase">Gestione esperti / garanti</h3>
        </div>
        <button
          onClick={openCreate}
          data-testid="admin-expert-new-button"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 min-h-[40px] hover:bg-black/85 transition-colors duration-200"
        >
          <UserPlus className="w-3.5 h-3.5" /> Nuovo esperto
        </button>
      </div>
      <p className="mt-2 text-xs text-[color:var(--text-2)] leading-5">
        Crea o modifica i garanti e cambia il loro stato. Solo gli esperti <strong>Attivi</strong> sono visibili nell'app e nel JSON-LD; “in trattativa” e “bozza” restano nascosti.
      </p>

      {experts === null ? (
        <p className="mt-4 text-sm text-[color:var(--text-2)] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carico…</p>
      ) : experts.length === 0 ? (
        <p className="mt-4 text-sm text-[color:var(--text-2)] py-6 text-center rounded-2xl border border-dashed border-[color:var(--hairline)]">Nessun esperto. Crea il primo garante.</p>
      ) : (
        <div className="mt-4 space-y-2.5">
          {experts.map((e) => (
            <div key={e.id} className="rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3" data-testid={`admin-expert-row-${e.id}`}>
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold leading-5 truncate">{e.nome} {e.cognome}</p>
                  <p className="font-mono text-[11px] text-[color:var(--text-2)] mt-0.5 truncate">
                    {[e.ruolo, e.brand].filter(Boolean).join(" · ") || "—"}
                    {(e.aree || []).length ? ` · aree: ${e.aree.join(", ")}` : ""}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => openEdit(e)}
                    aria-label="Modifica esperto"
                    data-testid={`admin-expert-edit-${e.id}`}
                    className="w-9 h-9 rounded-full border border-[color:var(--hairline)] flex items-center justify-center hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setConfirmDel(e)}
                    aria-label="Elimina esperto"
                    data-testid={`admin-expert-delete-${e.id}`}
                    className="w-9 h-9 rounded-full border border-[color:var(--hairline)] flex items-center justify-center hover:bg-red-50 text-[color:var(--danger)] transition-colors duration-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-extrabold uppercase tracking-wide rounded-full px-2 py-1" style={STATO_STYLE[e.stato]} data-testid={`admin-expert-stato-badge-${e.id}`}>
                  {e.stato === "attivo" ? "Attivo" : e.stato === "in_trattativa" ? "In trattativa" : "Bozza"}
                </span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-wide text-[color:var(--text-2)]">Stato:</span>
                  <select
                    value={e.stato}
                    disabled={busyId === e.id}
                    onChange={(ev) => changeStato(e, ev.target.value)}
                    data-testid={`admin-expert-stato-select-${e.id}`}
                    className="rounded-xl border border-[color:var(--hairline)] bg-white px-2.5 py-1.5 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ink)] disabled:opacity-60"
                  >
                    {STATI.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  {busyId === e.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-[color:var(--text-2)]" />}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog crea/modifica */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-expert-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "edit" ? "Modifica esperto" : "Nuovo esperto"}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="max-h-[62vh] overflow-y-auto space-y-3 pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold">Nome *</label>
                  <input className={inputCls} value={dialog.data.nome} onChange={(e) => setField("nome", e.target.value)} data-testid="admin-expert-field-nome" />
                </div>
                <div>
                  <label className="text-xs font-bold">Cognome *</label>
                  <input className={inputCls} value={dialog.data.cognome} onChange={(e) => setField("cognome", e.target.value)} data-testid="admin-expert-field-cognome" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold">Ruolo</label>
                  <input className={inputCls} value={dialog.data.ruolo} onChange={(e) => setField("ruolo", e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-bold">Brand</label>
                  <input className={inputCls} value={dialog.data.brand} onChange={(e) => setField("brand", e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold">Città</label>
                  <input className={inputCls} value={dialog.data.citta} onChange={(e) => setField("citta", e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-bold">Aree (separate da virgola)</label>
                  <input className={inputCls} value={dialog.data.aree} onChange={(e) => setField("aree", e.target.value)} placeholder="tecnica, tattica" data-testid="admin-expert-field-aree" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold">Bio</label>
                <textarea className={`${inputCls} min-h-[80px]`} value={dialog.data.bio} onChange={(e) => setField("bio", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-bold">Bio breve</label>
                <input className={inputCls} value={dialog.data.bio_compact} onChange={(e) => setField("bio_compact", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-bold">Citazione (endorsement)</label>
                <textarea className={`${inputCls} min-h-[70px]`} value={dialog.data.citazione} onChange={(e) => setField("citazione", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold">Logo URL</label>
                  <input className={inputCls} value={dialog.data.logo_url} onChange={(e) => setField("logo_url", e.target.value)} placeholder="/experts/…png" />
                </div>
                <div>
                  <label className="text-xs font-bold">Foto URL</label>
                  <input className={inputCls} value={dialog.data.foto_url} onChange={(e) => setField("foto_url", e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold">Stato</label>
                  <select className={inputCls} value={dialog.data.stato} onChange={(e) => setField("stato", e.target.value)} data-testid="admin-expert-field-stato">
                    {STATI.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold">Ordine</label>
                  <input type="number" className={inputCls} value={dialog.data.ordine} onChange={(e) => setField("ordine", e.target.value)} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setDialog(null)} className="rounded-full border border-[color:var(--hairline)] bg-white text-xs font-extrabold px-4 py-2.5 min-h-[40px] hover:bg-[color:var(--bg-subtle)] transition-colors duration-200">
              Annulla
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="admin-expert-save-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 min-h-[40px] hover:bg-black/85 disabled:opacity-60 transition-colors duration-200"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Salva
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma eliminazione */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="max-w-sm" data-testid="admin-expert-delete-dialog">
          <DialogHeader>
            <DialogTitle>Eliminare l'esperto?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[color:var(--text-2)]">
            Stai per eliminare <strong>{confirmDel?.nome} {confirmDel?.cognome}</strong>. L'operazione non è reversibile.
          </p>
          <DialogFooter>
            <button onClick={() => setConfirmDel(null)} className="rounded-full border border-[color:var(--hairline)] bg-white text-xs font-extrabold px-4 py-2.5 min-h-[40px] hover:bg-[color:var(--bg-subtle)] transition-colors duration-200">
              Annulla
            </button>
            <button onClick={doDelete} data-testid="admin-expert-confirm-delete-button" className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--danger)] text-white text-xs font-extrabold px-4 py-2.5 min-h-[40px] hover:brightness-95 transition-all duration-200">
              <Trash2 className="w-3.5 h-3.5" /> Elimina
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
